import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import { openWindow } from "../../Slices/windowsSlice";
import { getMenuIcon, subRouteOf } from "../sideMenu";
import WebtopLayoutContext from "../layout/WebtopLayoutContext";
import { hasShortcutForPath, addShortcut } from "../layout/layoutModel";

const DRAG_THRESHOLD = 6;
const DOUBLE_TAP_MS = 400;

/**
 * FolderPopover — opening a desktop "folder" (a top-level SIDEMENU section with
 * more than one sub-app) reveals its sub-apps as icons. Single-click launches a
 * sub-app in a window and closes the popover.
 *
 * DRAG A SUB-APP OUT ONTO THE DESKTOP to create a quick-access shortcut for it
 * (per-user, stored in the layout's recordShortcuts — so it's draggable on the
 * desktop afterward and is cleared by "Reset desktop"). Dropping back inside the
 * panel (or not moving) just launches/does nothing. Click backdrop / Esc closes.
 */
const FolderPopover = ({ section, onClose }) => {
  const dispatch = useDispatch();
  const { layout, updateLayout } = useContext(WebtopLayoutContext);
  const panelRef = useRef(null);
  const [drag, setDrag] = useState(null); // { sub, x, y } — ghost following the cursor
  const [selected, setSelected] = useState(null); // subId — single click selects (Windows style)
  const lastTapRef = useRef({ subId: null, time: 0 }); // manual double-click detection

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!section) return null;

  const launch = (sub) => {
    dispatch(openWindow({ path: subRouteOf(sub), title: sub.displayName }));
    onClose();
  };

  // Drop a sub-app onto the desktop → add a per-user shortcut at the drop point.
  const addToDesktop = (sub, x, y) => {
    const path = subRouteOf(sub);
    if (hasShortcutForPath(layout, path)) return; // already on the desktop
    const shortcut = {
      id: `sc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      path,
      label: sub.displayName,
      type: path.replace(/^\//, "").split("/")[0],
      x: Math.max(0, x),
      y: Math.max(0, y),
    };
    updateLayout({ recordShortcuts: addShortcut(layout, shortcut).recordShortcuts });
  };

  // Press a sub-app: a small move = drag-out (drop on desktop), no move = launch.
  const startDrag = (e, sub) => {
    if (e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    let moved = false;
    const onMove = (ev) => {
      if (!moved && Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) > DRAG_THRESHOLD) {
        moved = true;
      }
      if (moved) setDrag({ sub, x: ev.clientX, y: ev.clientY });
    };
    const onUp = (ev) => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setDrag(null);
      if (!moved) {
        // Manual double-click detection (native onDoubleClick is unreliable
        // alongside the mousedown-based drag, same as DesktopIcons).
        const now = Date.now();
        const last = lastTapRef.current;
        if (last.subId === sub.subId && now - last.time < DOUBLE_TAP_MS) {
          lastTapRef.current = { subId: null, time: 0 };
          launch(sub); // double click → open
        } else {
          lastTapRef.current = { subId: sub.subId, time: now };
          setSelected(sub.subId); // single click → select
        }
        return;
      }
      // Dropped outside the folder panel → it's on the desktop.
      const inPanel = panelRef.current && panelRef.current.contains(ev.target);
      if (!inPanel) {
        addToDesktop(sub, ev.clientX - 46, ev.clientY - 22); // center the icon under the cursor
        onClose();
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <div className="webtop-folder-backdrop" onMouseDown={onClose}>
      <div
        className="webtop-folder-panel"
        role="dialog"
        aria-label={`${section.name} folder`}
        ref={panelRef}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="webtop-folder-panel__head">
          <FontAwesomeIcon icon={getMenuIcon(section.name)} />
          <span className="webtop-folder-panel__title">{section.name}</span>
        </div>
        <div className="webtop-folder-grid">
          {section.Subfields.map((sub) => {
            const onDesktop = hasShortcutForPath(layout, subRouteOf(sub));
            return (
              <div
                key={sub.subId}
                className={`webtop-icon webtop-folder-app${onDesktop ? " webtop-folder-app--ondesktop" : ""}${selected === sub.subId ? " webtop-icon--selected" : ""}`}
                role="button"
                tabIndex={0}
                onMouseDown={(e) => startDrag(e, sub)}
                title={onDesktop ? `${sub.displayName} (on desktop)` : `${sub.displayName} — drag to desktop for quick access`}
              >
                <span className="webtop-icon__glyph webtop-icon__glyph--sub">
                  <FontAwesomeIcon icon={faFileLines} />
                </span>
                <span className="webtop-icon__label">{sub.displayName}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ghost that follows the cursor while dragging a sub-app out. */}
      {drag && (
        <div className="webtop-folder-dragghost" style={{ left: drag.x, top: drag.y }}>
          <span className="webtop-icon__glyph webtop-icon__glyph--sub">
            <FontAwesomeIcon icon={faFileLines} />
          </span>
          <span className="webtop-folder-dragghost__label">{drag.sub.displayName}</span>
        </div>
      )}
    </div>
  );
};

export default FolderPopover;
